// Normaliza as etapas vindas do response da rotina mensal.
const normalizeStages = (response) => {
  const stages = (response && response.value && response.value.etapas) || []
  return stages.map((stage) => ({ ...stage }))
}

// Garante que cada cliente tenha id_etapa_atual baseado na etapa.
const buildRoutineClients = (stages) => {
  return stages.flatMap((stage) =>
    (stage.clientes || []).map((client) => ({
      ...client,
      id_etapa_atual: client.id_etapa_atual || stage.id,
    }))
  )
}

// Cria um mapa de etapas com clientes zerados.
const buildStageMap = (stages) => {
  return stages.reduce((acc, stage) => {
    acc[stage.id] = { ...stage, clientes: [] }
    return acc
  }, {})
}

// Achata a lista de clientes vinda dos grupos do CRM.
const flattenClients = (groups) => {
  return (groups || []).flatMap((group) => group.clientes || [])
}

// Cria um mapa de clientes por id_cliente_app.
const buildClientIndex = (clients) => {
  return (clients || []).reduce((acc, client) => {
    if (client && client.id_cliente_app) {
      acc[client.id_cliente_app] = client
    }
    return acc
  }, {})
}

// Retorna o id da etapa default (menor ordem).
const getDefaultStageId = (stages) => {
  const sortedStages = [...stages].sort(
    (a, b) => (Number(a.ordem) || 0) - (Number(b.ordem) || 0)
  )
  return sortedStages[0] ? sortedStages[0].id : null
}

const fillClientData = (target = {}, source = {}) => {
  const fields = [
    'email',
    'telefone',
    'codigo_corretora',
    'relacionamento',
    'pl_total',
    'pl_potencial',
    'saldo_total_em_conta',
    'grupos_atividade_relacionamento',
    'foto_url',
  ]

  fields.forEach((field) => {
    const currentValue = target[field]
    const shouldFill =
      currentValue === undefined || currentValue === null || currentValue === ''

    if (shouldFill) {
      target[field] = source[field]
    }
  })
}

// Monta o cliente default para quem nao existe na rotina mensal.
const buildDefaultClient = ({ client, stageId }) => ({
  id: String(client.id_cliente_app),
  id_cliente_app: client.id_cliente_app,
  nome_cliente: client.nome_cliente || client.nome || '',
  foto_url: client.foto_url || null,
  email: client.email || '',
  telefone: client.telefone || '',
  codigo_corretora: client.codigo_corretora || '',
  relacionamento: client.relacionamento || {},
  pl_total: client.pl_total,
  pl_potencial: client.pl_potencial,
  saldo_total_em_conta: client.saldo_total_em_conta,
  tags: client.tags || [],
  id_etapa_anterior: null,
  id_etapa_atual: stageId,
  data_ultima_alteracao: null,
})

// Faz o merge entre clientes do CRM e itens da rotina mensal por etapa.
const mergeStages = ({ stages, allClients, routineClients }) => {
  const routineByClientId = routineClients.reduce((acc, item) => {
    if (item && item.id_cliente_app) {
      acc[item.id_cliente_app] = item
    }
    return acc
  }, {})
  const stageById = buildStageMap(stages)
  const defaultStageId = getDefaultStageId(stages)
  const clientIndex = buildClientIndex(allClients)
  const assignedClientIds = new Set()

  allClients.forEach((client) => {
    const clientId = client && client.id_cliente_app
    if (!clientId) return
    const routineItem = routineByClientId[clientId]
    const stageId =
      (routineItem && routineItem.id_etapa_atual) || defaultStageId
    const base = routineItem || buildDefaultClient({ client, stageId })
    if (base) {
      const fromClient = clientIndex[clientId]
      if (fromClient) {
        fillClientData(base, fromClient)
      }
    }
    if (stageById[stageId]) {
      stageById[stageId].clientes.push(base)
      assignedClientIds.add(clientId)
    }
  })

  routineClients.forEach((routineItem) => {
    const clientId = routineItem && routineItem.id_cliente_app
    const stageId = routineItem && routineItem.id_etapa_atual
    if (!clientId || assignedClientIds.has(clientId)) return
    const fromClient = clientIndex[clientId]
    if (fromClient) {
      fillClientData(routineItem, fromClient)
    }
    if (stageById[stageId]) {
      stageById[stageId].clientes.push(routineItem)
    }
  })

  return stages.map((stage) => {
    const mergedStage = stageById[stage.id] || { ...stage, clientes: [] }
    return {
      ...stage,
      clientes: mergedStage.clientes,
      total_clientes: mergedStage.clientes.length,
    }
  })
}

// Monta a lista final de etapas com clientes distribuídos por etapa.
export const buildMonthlyRoutineStages = ({ response, groups }) => {
  if (!response || response.status !== 200) return []

  const stages = normalizeStages(response)
  if (!stages.length) return []

  const allClients = flattenClients(groups)
  const routineClients = buildRoutineClients(stages)

  return mergeStages({ stages, allClients, routineClients })
}

const hasStagesWithClients = (data) =>
  Array.isArray(data) && data.some((stage) => Array.isArray(stage.clientes))

// Encontra uma etapa pelo id.
const findStageById = (stages, stageId) =>
  stages.find((stage) => stage.id === stageId)

// Adiciona o cliente na etapa e recalcula o total.
const addClientToStage = (stage, item) => {
  if (!stage) return
  stage.clientes = [...(stage.clientes || []), item]
  stage.total_clientes = stage.clientes.length
}

// Remove o cliente de todas as etapas e devolve o item atualizado.
const removeClientFromStages = (stages, data) => {
  let updatedItem = null
  let originalStageId = null

  stages.forEach((stage) => {
    if (!Array.isArray(stage.clientes)) return
    stage.clientes = stage.clientes.filter((item) => {
      const matchesById = item.id && data.id && item.id === data.id
      const matchesByClient =
        item.id_cliente_app &&
        data.id_cliente_app &&
        item.id_cliente_app === data.id_cliente_app
      if (matchesById || matchesByClient) {
        originalStageId = stage.id
        updatedItem = {
          ...item,
          ...data,
          id_etapa_anterior: data.id_etapa_anterior,
          id_etapa_atual: data.id_etapa_atual,
        }
        return false
      }
      return true
    })
    stage.total_clientes = stage.clientes.length
  })

  return { updatedItem, originalStageId }
}

// Move o cliente entre etapas mantendo o fallback na etapa original.
export const moveMonthlyRoutineItem = (monthlyRoutineData, data) => {
  if (!hasStagesWithClients(monthlyRoutineData) || !data) return false

  const { updatedItem, originalStageId } = removeClientFromStages(
    monthlyRoutineData,
    data
  )

  if (!updatedItem) return true

  const targetStageId =
    updatedItem.id_etapa_atual ||
    updatedItem.id_etapa_anterior ||
    originalStageId
  const targetStage = findStageById(monthlyRoutineData, targetStageId)

  if (targetStage) {
    addClientToStage(targetStage, updatedItem)
    return true
  }

  if (originalStageId) {
    const fallbackStage = findStageById(monthlyRoutineData, originalStageId)
    addClientToStage(fallbackStage, updatedItem)
  }

  return true
}
